import { SaveOutlined } from '@ant-design/icons';
import { GenericModelSchemaType } from '@smx-ui-model/types';
import { Button, notification } from 'antd';
import type { FormInstance } from 'antd/es/form/hooks/useForm';
import * as React from 'react';
import { memo, useContext, useEffect } from 'react';
import { useMutation } from 'react-relay';
import { MutationParameters, PayloadError } from 'relay-runtime';

import { useValidateInspectorForm } from './hook/use-validate';
import { SmxInspectorContext } from './inspector-provider';

interface FormButtonCreateProps {
    data: any;
    model: GenericModelSchemaType;
    connectionId?: string;
    form?: FormInstance;
}

const FormButtonSave: React.FC<FormButtonCreateProps> = ({ model, data, connectionId, form }) => {
    const { currentId } = useContext(SmxInspectorContext);
    const [commitCreate, isInFlightCreate] = useMutation(model.mutation!.create!);
    const [commitUpdate, isInFlightUpdate] = useMutation(model.mutation!.update!);
    const [api, contextHolder] = notification.useNotification();
    const { setIsOpen, setIsLoading } = useContext(SmxInspectorContext);
    const { validate } = useValidateInspectorForm(form);

    useEffect(() => {
        setIsLoading?.(isInFlightCreate || isInFlightUpdate);
    }, [isInFlightCreate, isInFlightUpdate]);

    const commitModel = (data: any, id?: string): MutationParameters => {
        // const optimisticUpdateTask = {
        //     updateTask: {
        //         id,
        //         ...data,
        //     },
        // };

        const optimisticUpdater = (store: any) => {
            if (id) {
                const oldModelStore = store.get(id);
                const fields = Object.keys(data);

                for (const keyField of fields) {
                    let newDataField = data[keyField];
                    if (model.prepareDataForMutation?.[keyField]) {
                        newDataField = model.prepareDataForMutation?.[keyField]?.(data[keyField]);
                    }
                    oldModelStore.setValue(newDataField, keyField);
                }
            }
        };

        const openNotificationWithIcon = () => {
            api['error']({
                message: 'Error',
                description: 'Something went wrong',
            });
        };

        const newInput = {
            ...data,
        };

        if (model.prepareDataForMutation) {
            const listFieldName = Object.keys(data);
            listFieldName.forEach((fieldName) => {
                if (model.prepareDataForMutation?.[fieldName]) {
                    newInput[fieldName] = model.prepareDataForMutation?.[fieldName]?.(data[fieldName]);
                }
            });
        }

        return {
            variables: {
                id,
                input: newInput,
                connections: connectionId ? [connectionId] : [],
            },
            // optimisticResponse: id ? optimisticUpdateTask : undefined,
            onCompleted: (response: any, errors: PayloadError[]) => {
                if (errors) {
                    console.log(
                        (
                            errors[0] as PayloadError & {
                                extensions: { debugMessage: string };
                            }
                        ).extensions.debugMessage,
                        errors,
                    );
                } else {
                    setIsOpen?.(false);
                    console.log('Task save successful', response);
                }
            },
            optimisticUpdater: id ? optimisticUpdater : undefined,
            onError: (error: any) => {
                openNotificationWithIcon();
                console.error('Mutation error:', error);
            },
        } as unknown as MutationParameters;
    };

    const handleClick = async () => {
        if (await validate()) {
            if (currentId) {
                commitUpdate(commitModel({ ...data }, currentId));
            } else {
                commitCreate(commitModel({ ...data }, undefined));
            }
        }
    };

    return (
        <Button onClick={handleClick} icon={<SaveOutlined />} loading={isInFlightCreate}>
            {contextHolder}
            Save
        </Button>
    );
};

export default memo(FormButtonSave);
